import { Box, useTheme } from "@mui/material";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { prettyPrintQuantity } from "encamp-shared/src/utils/prettyPrintUnits";
import { ReportingFacilityChemicalQuery, UnitType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { Control, useWatch } from "react-hook-form";

import { DisclosurePanel } from "./DisclosurePanel";
import { FormValues } from "./reportingFacilityChemicalFormValues";
import {
  canOverrideReportingUnit,
  describeReportingUnitOverrideOptionDisabledReason,
} from "./reportingUnitOverrideEligibility";
import { resolveDisplayAmounts } from "./resolveDisplayAmounts";

type Props = {
  rfc: ReportingFacilityChemicalQuery["reportingFacilityChemical"];
  control: Control<FormValues>;
  disabled: boolean;
};

export function StaffReportingUnitOverride({ rfc, control, disabled }: Props) {
  const { isStaff } = useCurrentUser();
  const theme = useTheme();

  const selectedUnit = useWatch({ control, name: "reportingUnitOverride" });

  if (!isStaff) {
    return null;
  }

  // The unit this chemical reports in absent any override: the true
  // pre-override unit while one is active, or the aggregated unit when none
  // ever applied. This is the base a new selection converts *from* — using
  // rfc.unit unconditionally would convert from an already-converted value
  // once an override is active, compounding rounding or even reversing a
  // dimension change. `resolveCombineTargetUnit` is deliberately not used
  // here either — it is a frontend chemical-merge preview aggregation never
  // calls, so it can (and for CA + liquid sulfuric acid, does) disagree with
  // the real RFC unit.
  const currentUnit = rfc.preOverrideUnit ?? rfc.unit ?? null;
  // The amount that unit describes, by the same reasoning.
  const currentMaxAmount = rfc.preOverrideMaxAmount ?? rfc.maxAmount ?? null;

  const unitItems = [
    { value: null, display: `Use default (${prettyPrintEnumValue(currentUnit)})` },
    ...Object.values(UnitType).flatMap((unit) => {
      const result = canOverrideReportingUnit(rfc, unit);
      if (result.ok) {
        return [{ value: unit, display: prettyPrintEnumValue(unit) }];
      }
      // A unit the jurisdiction does not accept is dropped rather than
      // greyed: it is a fixed property of the facility's state and nothing
      // staff can act on, so listing it only lengthens the list. CA hides
      // four of eight units this way, IN seven.
      //
      // The other refusals stay visible-but-disabled, because each names
      // something staff *can* fix (add a catalog density, enter an exact
      // amount) and the greyed label is the only place that fix appears.
      //
      // The exception is the unit already selected: dropping that would
      // leave the Select rendering blank against a value it has no option
      // for. Reachable when an override outlives the conditions that
      // allowed it — a facility changing state, or Oregon's list emptying
      // once a chemical's state of matter is cleared. It stays listed and
      // disabled; the blocking validation issue explains why it is invalid.
      if (result.reason === "jurisdictionDisallowsUnit" && unit !== selectedUnit) {
        return [];
      }
      return [
        {
          value: unit,
          display: `${prettyPrintEnumValue(unit)} (${describeReportingUnitOverrideOptionDisabledReason(result)})`,
          disabled: true,
        },
      ];
    }),
  ];

  // The dialog is maxWidth="lg"; none of these fields have any business
  // stretching that far. Computed once via the theme's own spacing unit
  // rather than passed as an sx callback — FormTextField's `sx` prop is
  // typed as plain SxProps (no Theme parameter), so a `(theme) => ...`
  // callback there doesn't type-check the way it does on FormSelect's
  // SxProps<Theme>.
  // 280px: fits the longest option ("Cubic Feet", "Metric Tons"). Applied to
  // the Select itself via `selectSx` rather than to the FormControl via `sx` —
  // the FormControl also wraps the helper text, and capping that at the
  // select's width breaks the conversion caption across lines mid-quantity
  // ("... will be reported as 7,500 / gals").
  const unitSelectMaxWidth = theme.spacing(35);
  const reasonMaxWidth = theme.spacing(70); // 560px: a sentence or two of prose, not the full dialog.

  const preview = resolveDisplayAmounts({
    maxAmount: currentMaxAmount,
    unit: currentUnit,
    density: rfc.chemical?.density,
    densityUnits: rfc.chemical?.densityUnits,
    targetUnit: selectedUnit,
    state: rfc.facility?.state,
  });

  // The select's own helperText slot carries the "default reporting unit"
  // caption when nothing is selected, and switches to the conversion preview
  // once a unit is picked — one row, in the position a user already looks
  // for guidance under an input, rather than a separate Typography under it.
  const selectHelperText = (() => {
    if (!selectedUnit) {
      // `currentUnit` is the unit this chemical reports in absent an override
      // — the true pre-override unit while one is active (so this reads
      // correctly even mid-edit, with the select cleared back to "Use
      // default" but the existing override not yet removed), or the
      // aggregated unit when none has ever applied.
      return `Default reporting unit: ${prettyPrintEnumValue(currentUnit)}`;
    }
    // Density-missing surfaces as a blocking field error instead (see
    // getReportingUnitOverrideValidationIssues); the no-unit-to-convert-from
    // pass-through case has nothing honest to state in this unit. Neither
    // belongs in the helper text.
    if (preview.densityMissing || !preview.isOverridden) {
      return undefined;
    }
    const from = prettyPrintQuantity({
      amount: currentMaxAmount,
      unit: currentUnit,
      state: rfc.facility?.state,
    });
    const to = prettyPrintQuantity({
      amount: preview.maxAmount,
      unit: preview.unit,
      state: rfc.facility?.state,
    });
    return `${from} will be reported as ${to}`;
  })();

  return (
    <DisclosurePanel data-reporting-unit-override>
      <FormSelect
        name="reportingUnitOverride"
        control={control}
        label="Report as unit"
        disabled={disabled}
        selectItems={unitItems}
        helperText={selectHelperText}
        selectSx={{ maxWidth: unitSelectMaxWidth }}
        sx={{ maxWidth: reasonMaxWidth }}
      />

      {/* The reason requirement is enforced by
          getReportingUnitOverrideValidationIssues, not by Controller `rules`:
          useValidatingForm installs a resolver, and react-hook-form ignores
          built-in field rules whenever a resolver is present. */}
      {selectedUnit && (
        // Both the width and the gap come from this wrapper rather than the
        // field's own `sx`: FormTextField only forwards `sx` in its readOnly
        // branch, so on a live field it is silently dropped.
        <Box sx={{ maxWidth: reasonMaxWidth, mt: 2 }}>
          <FormTextField
            name="reportingUnitOverrideReason"
            control={control}
            label="Reason *"
            textFieldProps={{ multiline: true, minRows: 2 }}
            disabled={disabled}
          />
        </Box>
      )}
    </DisclosurePanel>
  );
}
